/**
 * Performs a fetch request with default settings for API calls.
 */

import env from 'bench-environment';
import { ApiError, ErrorWithData } from './errors';

export const BASE_API_URL = `${env.location.apiRoot}/api/v1/`;
export const BASE_NODEWAY_API_URL = `${env.location.apiRoot}${env.location.nodewayApiPath}`;
export const BASE_BOOKKEEPING_API_URL = `${env.location.v2BookkeepingApiRoot}/`;

/**
 * Generic fetch helper with common logic for API requests.
 * @template R - The type of the response data
 * @param url - The URL to fetch
 * @param baseURL - The base URL to prepend
 * @param options - Fetch options
 * @param errorClass - The error class to throw on failure
 * @param errorMessage - The error message to use
 * @returns The parsed JSON response data or null for 204 responses
 * @throws {ErrorClass} Error object with response data on non-ok status
 */
async function fetchWithDefaults<R = any>(
  url: string,
  baseURL: string,
  options: RequestInit = {},
  errorClass: typeof ApiError | typeof ErrorWithData,
  errorMessage: string,
): Promise<R | null> {
  if (!url.startsWith('/')) {
    throw new Error('url must start with "/"');
  }

  if (!baseURL) {
    throw new Error(
      'baseURL is not configured. Check that window.location is available and hostname/protocol are set.',
    );
  }

  // build URL
  const fullURL = new URL(url.substring(1), baseURL).href;

  // Merge options with credentials and default Content-Type
  const mergedOptions: RequestInit = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(typeof options.headers === 'object' ? options.headers : {}),
    },
  };

  // Perform the fetch
  const response = await fetch(fullURL, mergedOptions);

  // Handle response
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const method = mergedOptions.method || 'GET';
    throw new errorClass(
      `${errorMessage}: ${method} ${fullURL} responded with ${response.status}`,
      errorData,
    );
  }

  return response.status === 204 ? null : response.json();
}

/**
 * Performs a fetch request with RestService-specific logic including credentials,
 * response handling, and error formatting. Mimics the fetch() API.
 * @template R - The type of the response data
 * @param url - The URL to fetch
 * @param options - Fetch options (same as standard fetch RequestInit)
 * @returns The parsed JSON response data or null for 204 responses
 * @throws {ApiError} Error object with response.data structure on non-ok status
 */
export async function apiFetch<R = any>(url: string, options: RequestInit = {}): Promise<R | null> {
  return fetchWithDefaults<R>(url, BASE_API_URL, options, ApiError, 'API request failed');
}

/**
 * Performs a fetch request to the Nodeway API with default settings.
 * @template R - The type of the response data
 * @param url - The URL to fetch (must start with '/')
 * @param options - Fetch options (same as standard fetch RequestInit)
 * @returns The parsed JSON response data or null for 204 responses
 * @throws {ErrorWithData} Error object with response data structure on non-ok status
 */
export async function nodewayFetch<R = any>(
  url: string,
  options: RequestInit = {},
): Promise<R | null> {
  return fetchWithDefaults<R>(
    url,
    BASE_NODEWAY_API_URL,
    options,
    ErrorWithData,
    'Nodeway API request failed',
  );
}

/**
 * Performs a fetch request to the bookkeeping API with default settings.
 * @template R - The type of the response data
 * @param url - The URL to fetch (must start with '/')
 * @param options - Fetch options (same as standard fetch RequestInit)
 * @returns The parsed JSON response data or null for 204 responses
 * @throws {ApiError} Error object with response.data structure on non-ok status
 */
export async function bookkeepingFetch<R = any>(
  url: string,
  options: RequestInit = {},
): Promise<R | null> {
  return fetchWithDefaults<R>(
    url,
    BASE_BOOKKEEPING_API_URL,
    options,
    ApiError,
    'Bookkeeping API request failed',
  );
}
